import { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import Select from 'react-select';
import { Stethoscope } from 'lucide-react';
import apiClient from '../api/client';

const BRAND_GREEN = '#0aa375';

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '30px',
    height: '30px',
    fontSize: '12px',
    borderColor: '#dee2e6',
    boxShadow: 'none',
    '&:hover': { borderColor: BRAND_GREEN },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px' }),
  indicatorsContainer: (base) => ({ ...base, padding: '0 2px' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
  clearIndicator: (base) => ({ ...base, padding: '0 4px' }),
  option: (base, state) => ({
    ...base,
    fontSize: '12px',
    backgroundColor: state.isSelected ? BRAND_GREEN : state.isFocused ? '#e6f7f2' : 'white',
    color: state.isSelected ? 'white' : '#333',
  }),
  singleValue: (base) => ({ ...base, fontSize: '12px' }),
  placeholder: (base) => ({ ...base, fontSize: '12px', color: '#adb5bd' }),
};

const filterLabelStyle = {
  fontSize: '10px',
  fontWeight: 600,
  color: 'black',
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

export default function DoctorInfoModal({ user, domain, onClose }) {
  const [doctorList, setDoctorList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [doctorNameFilter, setDoctorNameFilter] = useState('');
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [supervisedOpen, setSupervisedOpen] = useState(false);
  const [supervisedFor, setSupervisedFor] = useState(null);
  const [supervisedList, setSupervisedList] = useState([]);
  const [supervisedLoading, setSupervisedLoading] = useState(false);

  useEffect(() => {
    const fetchDoctorInfo = async () => {
      setLoading(true);
      setError(null);
      setDoctorNameFilter('');
      setSelectedOffice(null);
      setSelectedLocation(null);
      setSelectedSpecialty(null);
      setSupervisedOpen(false);
      setSupervisedFor(null);
      setSupervisedList([]);
      try {
        const res = await apiClient.get(`/api/users/doctor-info`, {
          params: { userId: user.szUserId, domain: domain, companyId: user.szCompanyId }
        });

        setDoctorList(Array.isArray(res.data) ? res.data : [res.data]);
      } catch (err) {
        const msg = err.response?.data?.message || "Failed to load doctor details";
        setError(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };

    if (user?.szUserId && domain && user?.szCompanyId) {
      fetchDoctorInfo();
    } else {
      setError("Missing user information");
      setLoading(false);
    }
  }, [user, domain]);

  const officeOptions = useMemo(() => {
    const map = new Map();
    doctorList.forEach((doc) => {
      if (!doc?.officeId) return;
      if (!map.has(doc.officeId)) {
        map.set(doc.officeId, {
          value: doc.officeId,
          label: `${doc.officeName || '—'} – ${doc.officeId}`,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [doctorList]);

  const locationOptions = useMemo(() => {
    const map = new Map();
    doctorList.forEach((doc) => {
      if (!doc?.locationId) return;
      if (!map.has(doc.locationId)) {
        map.set(doc.locationId, {
          value: doc.locationId,
          label: `${doc.locationName || '—'} – ${doc.locationId}`,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [doctorList]);

  const specialtyOptions = useMemo(() => {
    const map = new Map();
    doctorList.forEach((doc) => {
      if (!doc?.specialtyId) return;
      if (!map.has(doc.specialtyId)) {
        map.set(doc.specialtyId, {
          value: doc.specialtyId,
          label: `${doc.specialtyName || '—'} – ${doc.specialtyId}`,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [doctorList]);

  const filteredDoctorList = useMemo(() => {
    const nameTerm = doctorNameFilter.trim().toLowerCase();
    return doctorList
      .filter((doc) => {
        if (nameTerm && !(doc.doctorName || '').toLowerCase().includes(nameTerm)) return false;
        if (selectedOffice?.value && doc.officeId !== selectedOffice.value) return false;
        if (selectedLocation?.value && doc.locationId !== selectedLocation.value) return false;
        if (selectedSpecialty?.value && doc.specialtyId !== selectedSpecialty.value) return false;
        return true;
      })
      .sort((a, b) =>
        (a.doctorName || '').localeCompare(b.doctorName || '', undefined, { sensitivity: 'base' })
      );
  }, [doctorList, doctorNameFilter, selectedOffice, selectedLocation, selectedSpecialty]);

  const openSupervised = async (doc) => {
    setSupervisedFor(doc);
    setSupervisedOpen(true);
    setSupervisedList([]);
    setSupervisedLoading(true);
    try {
      const res = await apiClient.get(`/api/users/supervised-doctors`, {
        params: {
          domain,
          companyId: user.szCompanyId,
          supervisorDoctorId: doc.doctorId,
        },
      });
      setSupervisedList(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error('Failed to load supervised doctors');
      setSupervisedList([]);
    } finally {
      setSupervisedLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.65)',
        zIndex: 1060,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '12px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '1100px',
          background: 'white',
          borderRadius: '10px',
          overflow: 'hidden',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ background: BRAND_GREEN, color: 'white', padding: '12px 18px', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '15.5px' }}>
                Doctor Information | User Name : {user?.szUserName} | Company Name : {user?.szCompanyName}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              style={{ fontSize: '28px', background: 'none', border: 'none', color: 'white', lineHeight: 1, cursor: 'pointer' }}
            >
              ×
            </button>
          </div>
        </div>

        <div style={{ padding: '16px', overflow: 'auto', flex: 1 }}>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-success" role="status" />
              <p className="mt-3 text-muted">Loading doctor details...</p>
            </div>
          ) : error ? (
            <div className="text-center py-5 text-danger">{error}</div>
          ) : doctorList.length === 0 ? (
            <div className="text-center py-5 text-muted">No doctor information found</div>
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '12px',
                  flexWrap: 'nowrap',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '1.2 1 0', minWidth: 0 }}>
                  <span style={filterLabelStyle}>Search</span>
                  <input
                    type="text"
                    className="form-control"
                    style={{ height: '30px', fontSize: '12px', padding: '2px 8px', width: '100%' }}
                    value={doctorNameFilter}
                    onChange={(e) => setDoctorNameFilter(e.target.value)}
                    placeholder="Doctor name..."
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '1 1 0', minWidth: 0 }}>
                  <span style={filterLabelStyle}>Office</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Select
                      options={officeOptions}
                      value={selectedOffice}
                      onChange={setSelectedOffice}
                      placeholder="Name – ID"
                      isClearable
                      isSearchable
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '1 1 0', minWidth: 0 }}>
                  <span style={filterLabelStyle}>Location</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Select
                      options={locationOptions}
                      value={selectedLocation}
                      onChange={setSelectedLocation}
                      placeholder="Name – ID"
                      isClearable
                      isSearchable
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '1 1 0', minWidth: 0 }}>
                  <span style={filterLabelStyle}>Speciality</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Select
                      options={specialtyOptions}
                      value={selectedSpecialty}
                      onChange={setSelectedSpecialty}
                      placeholder="Name – ID"
                      isClearable
                      isSearchable
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </div>
                </div>
              </div>

              {filteredDoctorList.length === 0 ? (
                <div className="text-center py-4 text-muted" style={{ fontSize: '13px' }}>
                  No matching doctor records
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table
                    className="table table-bordered table-hover mb-0"
                    style={{ fontSize: '12px', minWidth: '960px', width: '100%' }}
                  >
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr>
                        <th style={{ whiteSpace: 'nowrap' }}>Doctor Name</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Doctor ID</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Office Name</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Office ID</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Location Name</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Location ID</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Specialty Name</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Specialty ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDoctorList.map((doc, index) => (
                        <tr key={`${doc.doctorId || 'doc'}-${doc.specialtyId || 'spec'}-${doc.locationId || 'loc'}-${index}`}>
                          <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            <span>{doc.doctorName || '—'}</span>
                            {(doc.isSupervisingDoctor || (doc.supervisedDoctorCount ?? 0) > 0) && (
                              <button
                                type="button"
                                onClick={() => openSupervised(doc)}
                                title={`${doc.supervisedDoctorCount || 0} supervised`}
                                style={{
                                  marginLeft: 6,
                                  border: 'none',
                                  background: 'none',
                                  cursor: 'pointer',
                                  color: BRAND_GREEN,
                                  padding: 0,
                                  verticalAlign: 'middle',
                                }}
                              >
                                <Stethoscope size={14} />
                                <span style={{ fontSize: 11, fontWeight: 700, marginLeft: 2 }}>
                                  {doc.supervisedDoctorCount || 0}
                                </span>
                              </button>
                            )}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>{doc.doctorId || '—'}</td>
                          <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{doc.officeName || '—'}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{doc.officeId || '—'}</td>
                          <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{doc.locationName || '—'}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{doc.locationId || '—'}</td>
                          <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{doc.specialtyName || '—'}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{doc.specialtyId || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {supervisedOpen && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
              padding: 16,
            }}
            onClick={() => setSupervisedOpen(false)}
          >
            <div
              style={{
                background: '#fff',
                width: '100%',
                maxWidth: 640,
                maxHeight: '70vh',
                overflow: 'hidden',
                borderRadius: 8,
                display: 'flex',
                flexDirection: 'column',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  background: BRAND_GREEN,
                  color: 'white',
                  padding: '10px 14px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14 }}>
                  Supervised doctors
                  {supervisedFor?.doctorName ? ` | ${supervisedFor.doctorName}` : ''}
                </div>
                <button
                  type="button"
                  onClick={() => setSupervisedOpen(false)}
                  style={{
                    fontSize: 22,
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    lineHeight: 1,
                    cursor: 'pointer',
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ padding: 16, overflow: 'auto', flex: 1 }}>
                {supervisedLoading ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm text-success" />
                  </div>
                ) : supervisedList.length === 0 ? (
                  <div className="text-muted" style={{ fontSize: 12 }}>No supervised doctors</div>
                ) : (
                  <table className="table table-bordered mb-0" style={{ fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th>Doctor</th>
                        <th>Location</th>
                        <th>Speciality</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supervisedList.map((s, i) => (
                        <tr key={`${s.doctorId}-${s.locationId}-${s.specialtyId}-${i}`}>
                          <td>{s.doctorName} – {s.doctorId}</td>
                          <td>{s.locationName} – {s.locationId}</td>
                          <td>{s.specialtyName || '—'} – {s.specialtyId || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div
                style={{
                  padding: '8px 12px',
                  borderTop: '1px solid #eee',
                  background: '#f8f9fa',
                  textAlign: 'right',
                  flexShrink: 0,
                }}
              >
                <button
                  type="button"
                  onClick={() => setSupervisedOpen(false)}
                  style={{
                    padding: '5px 14px',
                    background: '#6c757d',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        <div
          style={{
            padding: '10px 16px',
            borderTop: '1px solid #eee',
            background: '#f8f9fa',
            textAlign: 'right',
            flexShrink: 0,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '6px 20px',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}